/** Hydrates Topic detail data while Topic presentation stays in dedicated modules. */
import type { PrototypeApiClient } from './apiClient';
import { paintDetail, paintPremiumChips, paintTopicPrimaryAction } from './topicDetailPresentation';
import type { ChapterRow, ResumeResult, TopicDetail } from './topicDetailPresentationUtils';
import type { Root } from './prototypeTypes';
import { isCurrentTopicHydration, resetTopicInformationBand } from './topicDetailLifecycle';
import { rememberTopicReturnValue } from './topicReturnSnapshot';
import { paintTopicUnavailable } from './topicDetailViewPresentation';
import { type TopicChapterRequest } from './topicChapterRequest';
import type { TopicChapterListResponse } from './apiContracts';

export { resetTopicInformationBand } from './topicDetailLifecycle';

/** Resolves the direct-SSR Topic CTA without repainting server-rendered Topic content. */
export async function hydrateSsrTopicPrimaryAction(
	root: Root,
	api: PrototypeApiClient,
	topic: Pick<TopicDetail, 'id' | 'slug'>,
	plan: string = 'free',
	initialChapterIndex: number = 1,
): Promise<void> {
	const resumeResult = plan === 'visitor'
		? { loaded: true, response: null }
		: await api.getResumeCard(50).then((response) => ({ loaded: true, response })).catch(() => ({ loaded: false, response: null }));
	paintTopicPrimaryAction(root, topic, plan, resumeResult as ResumeResult, initialChapterIndex);
	if (resumeResult.loaded) rememberTopicReturnValue(root, 'resume', resumeResult.response);
}

export async function hydrateTopicDetail(
	root: Root,
	api: PrototypeApiClient,
	slug: string,
	plan: string = 'free',
	isCurrentNavigation: () => boolean = () => true,
	requestChapters: TopicChapterRequest = () => api.topicChapters(slug) as Promise<TopicChapterListResponse>,
): Promise<void> {
	if (!isCurrentNavigation()) return;
	const generation = resetTopicInformationBand(root);
	try {
		const detailPromise = api.topicDetail(slug).catch(() => null);
		const auxiliaryPromise = Promise.all([
			requestChapters().catch(() => null),
			api.topicKeywords(slug).catch(() => null),
			plan === 'visitor'
				? Promise.resolve({ loaded: true, response: null })
				: api.getResumeCard(50).then((response) => ({ loaded: true, response })).catch(() => ({ loaded: false, response: null })),
		]);
		const detailResp = await detailPromise;
		if (!isCurrentNavigation() || !isCurrentTopicHydration(root, generation)) return;
		if (!detailResp?.topic) {
			paintTopicUnavailable(root);
			return;
		}
		const [chapters, keywordResp, resumeResult] = await auxiliaryPromise;
		if (!isCurrentNavigation() || !isCurrentTopicHydration(root, generation)) return;
		const chapterResponse = chapters as { chapters?: ChapterRow[]; access?: { ownerAccess?: boolean } } | null;
		const detail = detailResp.topic as unknown as TopicDetail;
		paintDetail(root, detail, plan, keywordResp?.keywords, chapterResponse?.access);
		paintTopicPrimaryAction(root, detail, plan, resumeResult as ResumeResult, 1, null, chapterResponse?.chapters ?? []);
		rememberTopicReturnValue(root, 'detail', detailResp.topic);
		if (keywordResp?.keywords) rememberTopicReturnValue(root, 'keywords', keywordResp.keywords);
		if (resumeResult.loaded) rememberTopicReturnValue(root, 'resume', resumeResult.response);
		if (chapterResponse) paintPremiumChips(root, chapterResponse.chapters ?? [], plan, chapterResponse.access);
	} catch {
		if (isCurrentNavigation() && isCurrentTopicHydration(root, generation)) paintTopicUnavailable(root);
	}
}
